import Button from '@components/atoms/ui-kit/button.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import { useTranslation } from 'react-i18next';
import Input from '@components/atoms/ui-kit/input.tsx';
import { useState } from 'react';
import { useCreateSpeaker } from '@utils/api/organizations/speakers';

type Props = {
  show: boolean;
  onClose: () => void;
  setSpeaker: (value: string) => void;
};

const MAX_SPEAKER_LENGTH = 40;

// Helper to check if a name looks like a generic speaker pattern
const isGenericSpeakerName = (name: string): boolean => {
  const trimmed = name.trim().toLowerCase();
  if (/^speaker\s*\d+$/i.test(trimmed)) return true;
  if (/^\d+$/.test(trimmed)) return true;
  if (trimmed === 'no name' || trimmed === '') return true;
  return false;
};

const AddSpeaker = ({ show, onClose, setSpeaker }: Props) => {
  const { t } = useTranslation();
  const [newSpeaker, setNewSpeaker] = useState('');
  const createSpeakerMutation = useCreateSpeaker();

  const addSpeaker = async () => {
    const trimmed = newSpeaker.trim();
    if (trimmed) {
      setSpeaker(trimmed);
      setNewSpeaker('');
      onClose();

      // Save to directory if it's not a generic name. BE is idempotent
      // (api-scriptix #3785) so duplicates get back the existing row, not 400.
      if (!isGenericSpeakerName(trimmed)) {
        try {
          await createSpeakerMutation.mutateAsync({ name: trimmed });
        } catch {
          // Best-effort: speaker name is already applied locally; a directory
          // write failure shouldn't interrupt the user.
        }
      }
    }
  };

  return (
    <Modal
      isOpen={show}
      onClose={onClose}
      size="small"
      title={t('new-speaker')}
      caption={t('add-new-speaker-in-the-list')}
      footerActions={[
        <Button
          onClick={addSpeaker}
          label={t('add-speaker')}
          size="small"
          variant="primary"
          disabled={!newSpeaker.trim()}
        />
      ]}
    >
      <div className="space-y-2">
        <Input
          id="speaker"
          name="transcript-speaker-name"
          value={newSpeaker}
          onChange={e => setNewSpeaker(e.target.value)}
          placeholder={t('speaker-1')}
          size="small"
          className="w-full"
          maxLength={MAX_SPEAKER_LENGTH}
          autoComplete="on"
          autoFocus={true}
        />
        <div className="text-right text-xs text-gray-500 dark:text-zinc-400">
          {newSpeaker.length}/{MAX_SPEAKER_LENGTH}
        </div>
      </div>
    </Modal>
  );
};

export default AddSpeaker;
